/**
 * Tiny image-URL helpers used across artist / release / user cards to decide
 * whether we should render the upload, or fall back to a generative avatar.
 *
 * A placeholder URL is *truthy* but provides no real image — e.g.
 * `https://placehold.co/300x300`, which displays its own dimensions as text.
 * Treating those as "missing" lets the fallback render the proper themed
 * SVG avatar instead of an "300 x 300" stamp.
 */

import { isGeneratedInitials } from '@/lib/placeholder-photo';

const PLACEHOLDER_HOSTS = [
  'placehold.co',
  'placeholder.com',
  'via.placeholder.com',
  'placekitten.com',
  'dummyimage.com',
  'fakeimg.pl',
];

/** True when the URL is empty/falsy OR points at a known placeholder service. */
export function isPlaceholderImage(url?: string | null): boolean {
  if (!url || typeof url !== 'string') return true;
  const trimmed = url.trim();
  if (!trimmed) return true;
  if (isGeneratedInitials(trimmed)) return true;
  try {
    const u = new URL(trimmed);
    const host = u.hostname.toLowerCase();
    return PLACEHOLDER_HOSTS.some(h => host === h || host.endsWith(`.${h}`));
  } catch {
    return false; // not a parseable URL; let downstream decide
  }
}

/** Convenience: returns the URL only if it's a "real" image, else undefined. */
export function realImageOrUndef(url?: string | null): string | undefined {
  return isPlaceholderImage(url) ? undefined : (url || undefined);
}
